/**
 * Which of the two feature flag screens an operator lands on, and where each one lives.
 *
 * There are two: the original page, and the newer one this directory holds. `/features` is the entry
 * point every bookmark and the Mission Control header tab point at; it renders nothing itself and
 * sends the operator to whichever screen this module names.
 */

/** Where the preference is kept. Sits alongside this feature's other two keys. */
export const FEATURE_FLAG_UI_STORAGE_KEY = 'mission-control-feature-flag-ui';

export type FeatureFlagUi = 'new' | 'old';

/** The entry point. Renders nothing — see `FeatureFlagsUiRedirect`. */
export const FEATURE_FLAG_ENTRY_PATH = '/features';

/** The original page. Moved off the entry point so that path could become the redirect. */
export const FEATURE_FLAG_CLASSIC_PATH = '/features/classic';

/** The newer screens. Unmoved: three routes, a spec and Cypress selectors point at this prefix. */
export const FEATURE_FLAG_NEW_PATH = '/feature-flags';

/**
 * The screen a stored preference names.
 *
 * Only the literal `old` selects the original page; anything else — an absent key, a value from a
 * future version, a hand-edited one — resolves to the newer screens. Which screen is the default
 * therefore stays a decision in this function rather than one baked into every operator's browser,
 * and can be moved again later without having to migrate what is already stored.
 */
export function resolveStoredFeatureFlagUi(stored: string | null | undefined): FeatureFlagUi {
	return stored === 'old' ? 'old' : 'new';
}

/** Where a resolved preference sends the operator, without the Mission Control prefix. */
export function pathForFeatureFlagUi(ui: FeatureFlagUi): string {
	return ui === 'old' ? FEATURE_FLAG_CLASSIC_PATH : FEATURE_FLAG_NEW_PATH;
}

/**
 * The Mission Control prefix the given path is being served under.
 *
 * Both prefixes are live and a link has to stay on the one the operator arrived by, or switching
 * screens would silently move them between the two. Matches how `MissionControlHeader` decides the
 * same thing.
 */
export function missionControlPrefix(pathname: string): string {
	return pathname.includes('missioncontrol') ? '/missioncontrol' : '/control';
}

/** A feature flag path, prefixed for whichever Mission Control URL the operator arrived by. */
export function featureFlagUrl(pathname: string, path: string): string {
	return `${missionControlPrefix(pathname)}${path}`;
}
